import { useEffect } from 'react';

import { ProfileModel, profileApi, reportingApi } from '@pukka/api';
import { outletComparisonEvent } from '@pukka/clevertap/outletComparison';
import { withPersist, proxy, proxyMap, derive } from '@pukka/valtio';
import { UseQueryResult } from '@tanstack/react-query';

const MAX_OUTLET = 10;

interface DetailedOutlet {
  id: number;
  name: string;
  grossSales?: number;
  netSales?: number;
  grossProfit?: number;
  transaction?: number;
  grossMargin?: number;
  averageSalePerTransaction?: number;
  topItems?: string[];
}

interface DetailedOutletWithLoading extends DetailedOutlet {
  isLoading: boolean;
}

interface OutletComparisonState {
  outlets: Map<number, DetailedOutletWithLoading>;
  max: number;
  isFeatureAvailable: boolean;
  readonly onReplace: (...items: { id: number; name: string }[]) => Promise<void>;
  readonly onAdd: (...items: { id: number; name: string }[]) => Promise<void>;
  readonly onRemove: (...itemIds: number[]) => void;
  readonly onSetDate: (start?: Date, end?: Date) => void;
  readonly useData: () => Pick<UseQueryResult<ProfileModel.Response>, 'data' | 'status' | 'refetch' | 'error'>;
}

const { proxy: PersistedState } = withPersist(
  {
    selectedOutletIds: new Set<number>(),
    outletNames: new Map<number, string>(),
    startDate: new Date(NaN),
    endDate: new Date(NaN),
  },
  'outlet-comparison',
  {
    expiry: 1000 * 60 * 60 * 24, // 1 day expiry
    session: 'user', // removed on logout
  },
);

function mapPersistedStateIntoParams(state: typeof PersistedState, id: number) {
  return {
    id,
    name: state.outletNames.get(id) as string,
  };
}

async function getOutletDetail(item: { id: number; name: string }, startDate: Date, endDate: Date): Promise<Required<DetailedOutlet>> {
  return reportingApi.fetchReportingSummaryAndTopItems([item.id], startDate, endDate).then((res) => {
    return {
      id: item.id,
      name: item.name,
      grossSales: res.salesSummaryReport.grossSales || 0,
      netSales: res.salesSummaryReport.netSales || 0,
      transaction: res.salesSummaryReport.numberOfTransactions || 0,
      topItems: res.topItems.map((tI) => tI.name).slice(0, 3),
      grossProfit: res.grossProfitReport.grossProfit || 0,
      averageSalePerTransaction: res.salesSummaryReport.netSales / res.salesSummaryReport.numberOfTransactions || 0,
      grossMargin: parseFloat(((100 * res.grossProfitReport.grossProfit) / res.salesSummaryReport.grossSales).toFixed(2)) || 0,
    };
  });
}

function onBeforeFetch(state: OutletComparisonState, id: number, name: string) {
  state.outlets.set(id, {
    id,
    name,
    isLoading: true,
  });
}

function onAfterFetch(state: OutletComparisonState, id: number, data: Required<DetailedOutlet>) {
  state.outlets.set(id, {
    ...data,
    isLoading: false,
  });
}

function setMax(status: string, data: ProfileModel.Response | undefined, State: OutletComparisonState) {
  if (status === 'success' && data?.outlets && State.max !== Math.min(MAX_OUTLET, data.outlets.length)) {
    State.max = Math.min(MAX_OUTLET, data.outlets.length);
  }
}

function setFeatureAvailable(status: string, data: ProfileModel.Response | undefined, State: OutletComparisonState) {
  if (status === 'success' && State.isFeatureAvailable !== (data?.outlets?.length ?? 0) > 1) {
    State.isFeatureAvailable = (data?.outlets?.length ?? 0) > 1;
  }
}

function setDefaultSelected(status: string, data: ProfileModel.Response | undefined, State: OutletComparisonState) {
  if (status === 'success' && data?.outlets?.length === 2) {
    State.onAdd(
      ...data.outlets.map((outlet) => ({
        id: outlet.id as number,
        name: outlet.name as string,
      })),
    );
  }
}

const State = proxy({
  outlets: proxyMap<number, DetailedOutletWithLoading>(),
  max: MAX_OUTLET,
  isFeatureAvailable: false,
  async onReplace(...items: { id: number; name: string }[]) {
    PersistedState.selectedOutletIds = new Set();

    State.onAdd(...items);
  },
  async onAdd(...items: { id: number; name: string }[]) {
    const nonExistentItems = items.filter((item) => {
      const exist = State.outlets.has(item.id);

      PersistedState.selectedOutletIds.add(item.id);
      PersistedState.outletNames.set(item.id, item.name);

      PersistedState.selectedOutletIds = new Set(PersistedState.selectedOutletIds);

      if (!exist) {
        onBeforeFetch(State, item.id, item.name);
      }

      return !exist;
    });

    await Promise.all(
      nonExistentItems.map(async (item) => {
        const data = await getOutletDetail(item, PersistedState.startDate, PersistedState.endDate);

        onAfterFetch(State, item.id, data);
      }),
    );
  },
  onRemove(...itemIds: number[]) {
    itemIds.forEach((id) => PersistedState.selectedOutletIds.delete(id));
    PersistedState.selectedOutletIds = new Set(PersistedState.selectedOutletIds);
  },
  onSetDate(start?: Date, end?: Date) {
    if (start) {
      PersistedState.startDate = start;
    }

    if (end) {
      PersistedState.endDate = end;
    }

    // Reset outlets because of potentially different data
    State.outlets = proxyMap();
    State.onAdd(...[...PersistedState.selectedOutletIds].map(mapPersistedStateIntoParams.bind({}, PersistedState)));
  },
  useData() {
    const { data, status, refetch, error } = profileApi.useProfile({
      retry: 3,
      retryDelay: 500,
    });

    setMax(status, data, State);
    setFeatureAvailable(status, data, State);
    setDefaultSelected(status, data, State);

    useEffect(() => {
      State.onAdd(...[...PersistedState.selectedOutletIds].map(mapPersistedStateIntoParams.bind({}, PersistedState)));
    }, []);

    // send clevertap on the first load
    useEffect(() => {
      if (status === 'success') {
        outletComparisonEvent(State.outlets.size, data);
      }
    }, [status, data]);

    return { data, status, refetch, error };
  },
} as OutletComparisonState);

const DerivedState = derive({
  startDate: (get) => get(PersistedState).startDate,
  endDate: (get) => get(PersistedState).endDate,
  count: (get) => get(PersistedState).selectedOutletIds.size,
  selectedOutletIds: (get) => [...get(PersistedState).selectedOutletIds.keys()],
  canRemove: (get) => get(PersistedState).selectedOutletIds.size > 2,
  canAddMore: (get) => get(PersistedState).selectedOutletIds.size < get(State).max,
});

export {
  State,
  DerivedState,
  type DetailedOutlet,
  type DetailedOutletWithLoading,
  mapPersistedStateIntoParams as _mapPersistedStateIntoParams,
  setMax as _setMax,
  setFeatureAvailable as _setFeatureAvailable,
  getOutletDetail as _getOutletDetail,
};
